import { api } from '@goaltree/backend/convex/_generated/api'
import {
  type CoachAvatarStyle,
  audienceCoachVoice,
  coachFace,
  coachVoiceColor,
  coachVoiceId,
} from '@goaltree/backend/convex/lib/coach'
import { cn } from '@goaltree/ui/lib/utils'
import { useQuery } from 'convex/react'
import { useId } from 'react'

import { useIsAdmin } from '@/hooks/use-is-admin'
import { useIsPro } from '@/hooks/use-is-pro'

function useVisibleVoice(voice: string | undefined) {
  const isAdmin = useIsAdmin()
  return audienceCoachVoice(voice, isAdmin)
}

// Per-persona expression: mouth curve (idle smile), eye size, and brow shape.
const FACES: Record<string, { mouth: string; eyeRy: number; brow?: 'firm' | 'soft' }> = {
  eve: { mouth: 'M34 59 Q50 73 66 59', eyeRy: 6.5 },
  ara: { mouth: 'M37 60 Q50 68 63 60', eyeRy: 5.5, brow: 'soft' },
  rex: { mouth: 'M35 62 Q50 66 65 57', eyeRy: 5, brow: 'firm' },
  sal: { mouth: 'M38 60 Q50 66 62 60', eyeRy: 5 },
  leo: { mouth: 'M37 62 Q50 64 63 62', eyeRy: 4.5, brow: 'firm' },
  altair: { mouth: 'M36 60 Q50 67 64 60', eyeRy: 5.5 },
  atlas: { mouth: 'M35 61 Q50 66 65 59', eyeRy: 5, brow: 'firm' },
  carina: { mouth: 'M35 59 Q50 70 65 59', eyeRy: 6 },
  celeste: { mouth: 'M37 60 Q50 67 63 60', eyeRy: 5.5, brow: 'soft' },
  cosmo: { mouth: 'M36 60 Q50 68 64 59', eyeRy: 5.5 },
  helios: { mouth: 'M34 59 Q50 72 66 59', eyeRy: 6.5 },
  custom: { mouth: 'M36 60 Q50 69 64 60', eyeRy: 5.5 },
}

// Live call video for this coach when the user picked that style (Pro), else null.
export function useCoachFace(voice: string | undefined) {
  const prefs = useQuery(api.preferences.getMyPreferences) as
    | { coachAvatarStyle?: CoachAvatarStyle }
    | undefined
  const { isPro } = useIsPro()
  return coachFace(useVisibleVoice(voice), isPro ? prefs?.coachAvatarStyle : 'illustrated')
}

// Still portrait for a coach saved as realistic. Call video stays on useCoachFace.
export function useSavedRealisticCoach(voice: string | undefined) {
  const prefs = useQuery(api.preferences.getMyPreferences) as
    | { coachAvatarStyle?: CoachAvatarStyle }
    | undefined
  return coachFace(useVisibleVoice(voice), prefs?.coachAvatarStyle)
}

export function CoachAvatar(props: {
  voice: string | undefined
  size?: number
  speaking?: boolean
  className?: string
}) {
  const face = useSavedRealisticCoach(props.voice)
  return face ? (
    <RealisticCoachAvatar image={face.image} {...props} />
  ) : (
    <IllustratedCoachAvatar {...props} />
  )
}

export function RealisticCoachAvatar({
  image,
  size = 40,
  speaking = false,
  className,
}: {
  image: string
  size?: number
  speaking?: boolean
  className?: string
}) {
  return (
    <span
      aria-hidden
      className={cn(
        'relative block shrink-0 rounded-full transition-shadow duration-300',
        speaking && 'shadow-[0_0_16px_4px_color-mix(in_srgb,var(--primary)_50%,transparent)]',
        className,
      )}
      // Same 4% inset as the illustrated face (r=46 of 100) so both looks match in size.
      style={{ width: size, height: size, padding: size * 0.04 }}
    >
      <span className="block size-full overflow-hidden rounded-full">
        <img
          src={image}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          decoding="async"
          // The photos are head-and-shoulders; a light zoom centres the face, framed
          // like the homepage's cropped headshots.
          className="size-full origin-[50%_46%] scale-[1.3] object-cover"
        />
      </span>
    </span>
  )
}

// An animated SVG "face" for a coach voice — idle bob + blink, and a mouth that
// talks while the voice is playing. Self-contained (no assets, no deps).
export function IllustratedCoachAvatar({
  voice,
  size = 40,
  speaking = false,
  className,
}: {
  voice: string | undefined
  size?: number
  speaking?: boolean
  className?: string
}) {
  const id = voice === 'custom' ? 'custom' : coachVoiceId(voice)
  const color = coachVoiceColor(voice)
  const face = FACES[id] ?? FACES.eve
  const gid = useId()

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      className={cn('shrink-0', className)}
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={gid} cx="38%" cy="30%" r="78%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.5" />
          <stop offset="42%" stopColor="#ffffff" stopOpacity="0" />
        </radialGradient>
      </defs>
      <g className="coach-face">
        {/* Gloss sits on an opaque head: a half-transparent gradient stop let
            whatever was behind the avatar show through as a grey smudge. */}
        <circle cx="50" cy="50" r="46" fill={color} />
        <circle cx="50" cy="50" r="46" fill={`url(#${gid})`} />
        {/* grounding shadow for depth */}
        <ellipse cx="50" cy="74" rx="34" ry="16" fill="#000000" opacity="0.1" />

        {/* brows */}
        {face.brow === 'firm' && (
          <>
            <path d="M30 35 L44 38" stroke="#00000055" strokeWidth="3" strokeLinecap="round" />
            <path d="M70 35 L56 38" stroke="#00000055" strokeWidth="3" strokeLinecap="round" />
          </>
        )}
        {face.brow === 'soft' && (
          <>
            <path
              d="M31 37 Q37 33 43 37"
              stroke="#ffffff88"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
            />
            <path
              d="M57 37 Q63 33 69 37"
              stroke="#ffffff88"
              strokeWidth="2.5"
              fill="none"
              strokeLinecap="round"
            />
          </>
        )}

        {/* eyes */}
        <g className="coach-eye">
          <ellipse cx="37" cy="46" rx="5" ry={face.eyeRy} fill="#ffffff" />
          <circle cx="38" cy="47" r="2.6" fill="#1f2937" />
        </g>
        <g className="coach-eye">
          <ellipse cx="63" cy="46" rx="5" ry={face.eyeRy} fill="#ffffff" />
          <circle cx="62" cy="47" r="2.6" fill="#1f2937" />
        </g>

        {/* mouth */}
        {speaking ? (
          <ellipse cx="50" cy="62" rx="8" ry="7" fill="#3f1220cc" className="coach-mouth-talk" />
        ) : (
          <path
            d={face.mouth}
            stroke="#3f1220cc"
            strokeWidth="3.5"
            fill="none"
            strokeLinecap="round"
          />
        )}
      </g>
    </svg>
  )
}

// Coach launcher face resolved to the user's chosen voice — a drop-in for the
// Brain icon in headers, the sidebar, and FABs.
export function CoachLauncherFace({ size = 22, className }: { size?: number; className?: string }) {
  const prefs = useQuery(api.preferences.getMyPreferences)
  const voice = (prefs as { coachVoice?: string } | undefined)?.coachVoice
  return <CoachAvatar voice={voice} size={size} className={className} />
}
